/**
 * Otto session list for a given pageType — powers the conversations list.
 *
 * Returns the user's non-deleted sessions sorted by activity, newest
 * first. Both ACTIVE and ARCHIVED rows are returned; the caller renders
 * them differently. Empty (never-messaged) rows are filtered server-side.
 */

import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getOttoApi} from '../api/otto-api-client';
import {ottoSessionKeys} from '../query-keys';
import type {OttoSessionSummary, PageType} from '../types/otto-models';

type UseOttoSessionsOptions = {
  pageType: PageType;
  /** Skip the fetch when the panel is closed and the data isn't needed. */
  enabled?: boolean;
};

export type UseOttoSessionsReturn = {
  sessions: OttoSessionSummary[];
  isLoading: boolean;
  isError: boolean;
};

export function useOttoSessions({
  pageType,
  enabled = true,
}: UseOttoSessionsOptions): UseOttoSessionsReturn {
  const tenantId = useRequiredTenantId();
  const api = useMemo(() => getOttoApi(), []);

  const query = useQuery({
    queryKey: ottoSessionKeys.list(tenantId, pageType),
    async queryFn() {
      return api.listSessions(pageType);
    },
    enabled,
    // List is only mutated through Otto itself — invalidations come from
    // useOttoMutations on archive / restore / delete / new-conversation.
    staleTime: Number.POSITIVE_INFINITY,
  });

  return {
    sessions: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
